import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { LeaveRequest, LeaveRequestStatus } from '../store/leave-request.model';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { Process } from '../../shared';
import {
    takeWhile,
    map,
    withLatestFrom,
    tap,
    filter,
    distinctUntilChanged
} from 'rxjs/operators';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IUserInfo, UserRoleEnum, IUserColor } from '../../user-info';
import { IDirectReport } from '../../lookups';
import { SelectItem } from '../../shared/efaact-primeng.module';
import moment from 'moment';
import * as fromStore from '../store';
import * as fromLookups from '../../lookups';
import * as fromErrors from '../../shared';
import * as fromUser from '../../user-info';
import * as fromShared from '../../shared';
import * as fromCommon from '../../shared';
import * as fromCompanySetting from '../../company-setting';
import { PayrollItem } from '../../payroll/item';

@Component({
    selector: 'ef-leave-request-feature',
    templateUrl: './leave-request.feature.html',
    styleUrls: ['./leave-request.feature.scss'],
    standalone: false
})
export class LeaveRequestFeatureComponent implements OnInit, OnDestroy {
    process$: Observable<Process>;

    leaverequests$: Observable<LeaveRequest[]>;
    requestStatusSelectItems$: Observable<SelectItem[]>;
    tasks$: Observable<fromLookups.ITask[]>;
    payrollItems$: Observable<PayrollItem[]>;
    serviceItems$: Observable<fromLookups.IServiceItem[]>;
    companySettings$: Observable<fromCompanySetting.CompanySetting[]>;
    reportRequests$: Observable<LeaveRequest[]>;
    userInfo$: Observable<IUserInfo>;
    directReports$: Observable<Array<IDirectReport>>;
    userWebColors$: Observable<Array<IUserColor>>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    private userInfo: IUserInfo;
    private active = true;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    private DateRangeSubject = new ReplaySubject<{
        startDate: Date;
        endDate: Date;
    }>(1);
    private SetUserSubject = new ReplaySubject<{ roleId: number; EID: string }>(
        1
    );
    private SetRoleSubject = new ReplaySubject<number>(1);
    constructor(
        private store: Store<fromStore.State>,
        private log: LoggingService
    ) {}

    ngOnInit() {
        this.setupObservables();
        this.setupSubscriptions();

        this.store.dispatch(new fromUser.LoadUserInfo());
        this.store.dispatch(new fromLookups.LoadPayrollItems());
        this.store.dispatch(new fromLookups.LoadServiceItems());
        this.store.dispatch(
            fromCompanySetting.getCompanySetting({
                SettingType: 'LRCreateAuth'
            })
        );
    }

    setupSubscriptions() {
        this.store
            .pipe(
                select(fromUser.getUserInfo),
                takeWhile(() => this.active),
                distinctUntilChanged()
            )
            .subscribe(userInfo => {
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:userInfo',
                    userInfo
                });
                this.userInfo = userInfo;
                this.store.dispatch(new fromLookups.LoadDirectReports({user: userInfo, activeOnly: false }));
                this.SetUserSubject.next({
                    roleId: userInfo.DefaultRoleGroupID,
                    EID: userInfo.EmployeeListID
                });
            });
        this.DateRangeSubject.pipe(distinctUntilChanged())
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.SetUserSubject),
                withLatestFrom(this.process$.pipe(filter(x => !!x)))
            )
            .subscribe(([[dates, role], process]) => {
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:(DateRangeSubject, SetRoleSubject, userInfo)',
                    dates,
                    role,
                    process
                });
                this.store.dispatch(
                    fromStore.getLeaveRequests({
                        userId: role.EID,
                        userRole:
                            process.ProcessID === 69 ? 12028 : role.roleId,
                        startDate: dates.startDate,
                        endDate: dates.endDate
                    })
                );
            });

        this.SetRoleSubject.pipe(
            takeWhile(() => this.active),
            distinctUntilChanged(),
            withLatestFrom(this.store.pipe(select(fromUser.getUserInfo)))
        ).subscribe(([role, user]) => {
            this.SetUserSubject.next({
                roleId: role,
                EID: user.EmployeeListID
            });
        });
        this.process$.pipe(takeWhile(() => this.active)).subscribe(process => {
            this.store.dispatch(
                new fromLookups.LoadUserTasks({
                    ProcessID: process.ProcessID,
                    AsOfDate: new Date()
                })
            );
        });

        this.ShowErrorSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.process$)
        ).subscribe(([event, process]) => {
            this.store.dispatch(
                new fromShared.Errors.SetShowErrors({
                    open: event.open,
                    MsgEntityID: event.MsgEntityID,
                    MsgProcessID: process.ProcessID
                })
            );
        });
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.leaverequests$ = this.store.pipe(
            select(fromStore.selectAll),
            withLatestFrom(this.process$),
            filter(([r, p]) => !!(r && p)),
            tap(([requests, process]) =>
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:leaverequests$',
                    requests,
                    process
                })
            ),
            map(([l, process]) =>
                l.map(r => ({
                    ...r,
                    dates: [
                        new Date(r.EmpAuthStartDate),
                        new Date(r.EmpAuthEndDate)
                    ],
                    IsReadOnly:
                        process.ProcessID !== 69 ||
                        !(
                            r.RequestStatusID === LeaveRequestStatus.Saved ||
                            r.RequestStatusID === LeaveRequestStatus.Signed ||
                            r.RequestStatusID === LeaveRequestStatus.Disapproved
                        )
                }))
            ),
            tap(requests =>
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:leaverequests$:mapped',
                    requests
                })
            )
        );
        this.requestStatusSelectItems$ = this.leaverequests$.pipe(
            map(items =>
                fromShared.uniqBy(
                    items.map(item => ({
                        label: item.RequestStatus,
                        value: item.RequestStatus
                    })),
                    x => x.value
                )
            )
        );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors)
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.leaverequests$.pipe(filter(x => !!x))
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:errors$:filtered',
                    selected
                })
            )
        );
        this.companySettings$ = this.store.pipe(select(fromCompanySetting.selectAll));
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.tasks$ = this.store.pipe(select(fromLookups.getUserTasks));
        this.payrollItems$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup),
            map(items => (items || []).filter(x => x.IsActive !== false))
        );
        this.serviceItems$ = this.store.pipe(select(fromLookups.getServiceItemsLookup));
        this.userWebColors$ = this.store.pipe(
            select(fromUser.getUserColors),
            tap(x =>
                this.log.verbose({
                    type: 'LeaveRequestFeatureComponent:userWebColors$',
                    x
                })
            )
        );
        this.directReports$ = this.store.pipe(
            select(fromLookups.getDirectReportsLookup),
            map(a => a.filter(d => d.IsActive))
        );
    }
    onSave($event: LeaveRequest) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onSave',
            $event
        });
        this.store.dispatch(fromStore.putLeaveRequests({ requests: [$event] }));
    }
    onApprove($event: LeaveRequest[]) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onApprove',
            $event
        });
        this.store.dispatch(fromStore.putLeaveRequests({ requests: $event }));
    }
    onUnapprove($event: LeaveRequest[]) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onUnapprove',
            $event
        });
        this.store.dispatch(fromStore.putLeaveRequests({ requests: $event }));
    }
    onDisapprove($event: LeaveRequest[]) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onDisapprove',
            $event
        });
        this.store.dispatch(fromStore.putLeaveRequests({ requests: $event }));
    }

    onDelete($event: LeaveRequest) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onDelete',
            $event
        });
        this.store.dispatch(
            fromStore.deleteLeaveRequests({ ids: [$event.AuthRequestID] })
        );
    }
    onSetRole($event: number) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onSetRole',
            $event
        });
        this.SetRoleSubject.next($event);
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    onSetDateRange($event: { startDate: Date; endDate: Date }) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:onSetDateRange',
            $event
        });
        this.DateRangeSubject.next($event);
    }
    onShowError(event: fromShared.Errors.IShowErrors) {
        this.ShowErrorSubject.next(event);
    }
}
